import { useState, useMemo } from 'react';
import { Box, SvgIcon, Grid, Theme } from '@mui/material';

import { SearchField } from '@components/search/SearchField';
import { RadioGroupField } from '@components/radioButton/RadioGroupField';
import { ExclamationCircleSolid } from '@assets/svgs';
import { Tooltip } from '@components/tooltip/Tooltip';
import { LazyJsonViewer } from '@components/json/LazyJsonViewer';
import { adjustFontSize } from '@theme/styleOverrides/typography';

export const RatingReportJsonViewer = ({ ratingReport }: { ratingReport: any }) => {
  const [searchValue, setSearchValue] = useState<string>('');
  const [highlightOnly, setHighlightOnly] = useState<'on' | 'off'>('off');

  const validSearchValue = useMemo(() => {
    return searchValue && searchValue !== '';
  }, [searchValue]);
  // this is how the package (@textea/json-viewer) examples does it... so ignoring our warning

  const KeyRenderer = ({ path }: any) => {
    const value = path.slice(-1)[0];
    const highlight =
      validSearchValue && typeof value === 'string' && value?.toLowerCase().includes(searchValue!.toLowerCase());

    if (!highlight) {
      return value;
    }

    const parts = value.split(new RegExp(`(${searchValue})`, 'gi'));
    return (
      <Box component='span'>
        {parts.map((part, _i) => (
          <Box
            key={Math.random()}
            component='span'
            sx={part.toLowerCase() === searchValue!.toLowerCase() ? { background: 'yellow' } : {}}
          >
            {part}
          </Box>
        ))}
      </Box>
    );
  };
  KeyRenderer.when = () => {
    return true; // always apply the above to check for highlight
  };

  const contentToDisplay = useMemo(() => {
    try {
      if (highlightOnly === 'on') {
        return ratingReport;
      }
      if (!searchValue || searchValue === '') return ratingReport;

      const searchLower = searchValue.toLowerCase();

      const filteredPoliciesCoverages: any[] = [];
      ratingReport.ratingDetails?.policy?.coverages?.forEach((item: any) => {
        const include = item?.name?.toLowerCase().includes(searchLower);
        if (!include) {
          return;
        }

        filteredPoliciesCoverages.push(item);
      });

      const filteredInsuranceLines: any = [];
      ratingReport.ratingDetails.insuranceLines?.forEach((il: any) => {
        const quoteItemsToInclude: any[] = [];

        il.quoteItems.forEach((qi: any) => {
          // get all qi.generalFactors where the key includes the search value
          const generalFactorsMatch: any = {};
          Object.keys(qi.generalFactors)?.forEach((key) => {
            const include = key?.toLowerCase().includes(searchLower);
            if (!include) {
              return;
            }
            generalFactorsMatch[key] = qi.generalFactors[key];
          });

          const coveragesMatch: any = [];
          Object.values(qi.coverages)?.forEach((item: any) => {
            const include = item?.name?.toLowerCase().includes(searchLower);
            if (!include) {
              return;
            }

            coveragesMatch.push(item);
          });

          if (Object.values(generalFactorsMatch).length > 0 || coveragesMatch.length > 0) {
            quoteItemsToInclude.push({
              ...qi,
              generalFactors: generalFactorsMatch,
              coverages: coveragesMatch,
            });
          }
        });

        // if any quote items matched the search value, include the insurance line
        if (quoteItemsToInclude.length > 0) {
          filteredInsuranceLines.push({
            ...il,
            quoteItems: quoteItemsToInclude,
          });
        }
      });

      const newRatingReport = {
        ...ratingReport,
        ratingDetails: {
          ...ratingReport.ratingDetails,
          policy: {
            ...ratingReport.policy,
            coverages: filteredPoliciesCoverages ?? [],
          },
          insuranceLines: filteredInsuranceLines ?? [],
        },
      };

      return newRatingReport;
    } catch (e) {
      console.error('Unable to filter rating report', e);
      return ratingReport;
    }
  }, [searchValue, highlightOnly, ratingReport]);

  return (
    <Box>
      <LazyJsonViewer
        value={contentToDisplay}
        sx={{
          overflowY: 'auto',
          fontSize: ({ palette }: Theme) => `${adjustFontSize(11, palette.fontSize)}px`,
          fontWeight: 300,
          backgroundColor: 'white',
        }}
        keyRenderer={KeyRenderer}
      />
      <Grid container spacing={2} sx={{ position: 'absolute', bottom: { xs: 3, md: 16 }, right: 20, width: '640px' }}>
        <Grid>
          <RadioGroupField
            onChange={(e: any, value: string) => setHighlightOnly(value as 'on' | 'off')}
            defaultValue={highlightOnly}
            options={[
              { value: 'on', label: 'Highlight Only' },
              { value: 'off', label: 'Filter Results' },
            ]}
            sxProps={{
              label: {
                color: 'common.black',
              },
            }}
          />
        </Grid>
        <Grid>
          <SearchField onChange={(val) => setSearchValue(val)} />{' '}
        </Grid>
        <Grid>
          <Tooltip
            title={
              <Box>
                Will attempt to search the following: (only keys can be highlighted)
                <li>Policy</li>
                <li style={{ marginLeft: '14px' }}>Coverage Names</li>
                <li>Insurance Line Quote Items</li>
                <li style={{ marginLeft: '14px' }}>General Factors Keys</li>
                <li style={{ marginLeft: '14px' }}>Coverage Names</li>
              </Box>
            }
            sx={{
              maxWidth: 300,
            }}
          >
            <SvgIcon sx={{ color: 'background.secondary', marginLeft: '-8px' }}>
              <ExclamationCircleSolid width='16px' />
            </SvgIcon>
          </Tooltip>
        </Grid>
      </Grid>
    </Box>
  );
};
